
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';

interface RichTextEditorProps {
  title?: string;
  initialContent: string;
  onSave: (content: string) => void;
  onSaved?: () => void;  // Added missing prop that's being used by other components
  saveStatus?: 'idle' | 'saving' | 'success' | 'error';
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
  title, 
  initialContent, 
  onSave,
  onSaved,
  saveStatus = 'idle'
}) => {
  const [content, setContent] = useState(initialContent);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setContent(initialContent);
    setIsDirty(false);
  }, [initialContent]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    setIsDirty(true);
  };

  const handleSave = () => {
    onSave(content);
    setIsDirty(false);
    
    // Call onSaved callback if provided after saving
    if (saveStatus === 'success' && onSaved) {
      setTimeout(() => {
        onSaved();
      }, 500); // Small delay to allow UI to update first
    }
  };

  // Call onSaved when saveStatus transitions to 'success'
  useEffect(() => {
    if (saveStatus === 'success' && onSaved) {
      const timer = setTimeout(() => {
        onSaved();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus, onSaved]);

  const getSaveButtonText = () => {
    switch (saveStatus) {
      case 'saving': return 'Saving...';
      case 'success': return 'Saved!';
      case 'error': return 'Error! Try Again';
      default: return 'Save Changes';
    }
  };

  const getSaveButtonVariant = () => {
    switch (saveStatus) {
      case 'saving': return 'outline';
      case 'success': return 'success';
      case 'error': return 'destructive';
      default: return 'default';
    }
  };

  return (
    <Card className="border">
      {title && <div className="px-4 py-2 border-b bg-muted/50">{title}</div>}
      <CardContent className="p-0">
        <textarea
          className="w-full h-96 p-4 text-sm font-mono focus:outline-none resize-none"
          value={content}
          onChange={handleChange}
          placeholder="Enter content here..."
        />
      </CardContent>
      <CardFooter className="flex justify-between px-4 py-2 border-t bg-muted/20">
        <div className="text-sm text-muted-foreground">
          {isDirty ? "Unsaved changes" : "No changes"}
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saveStatus === 'saving' || !isDirty}
          variant={getSaveButtonVariant() as any}
        >
          {getSaveButtonText()}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default RichTextEditor;
